import React, { useState } from 'react';
import { Input } from '@/shared/components/ui/Input';
import { FormSection } from '@/shared/components/ui/FormSection';
import { FormField } from '@/shared/components/ui/FormField';
import { useUpdatePassword } from '../hooks';
import { Icon } from '@/shared/icons';
import { useAccountSaveButton } from './account-save-context';

/**
 * Security Section
 * Allows user to change their password. The primary "Atualizar senha" action
 * is published to the page header (single primary button).
 */
export const SecuritySection: React.FC = () => {
  const updatePasswordMutation = useUpdatePassword();

  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showCurrentPassword, setShowCurrentPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  const [successMessage, setSuccessMessage] = useState('');
  const [errorMessage, setErrorMessage] = useState('');

  const handleChangePassword = async (): Promise<void> => {
    setSuccessMessage('');
    setErrorMessage('');

    // Validation
    if (!currentPassword || !newPassword || !confirmPassword) {
      setErrorMessage('Todos os campos são obrigatórios');
      return;
    }

    if (newPassword.length < 6) {
      setErrorMessage('A nova senha deve ter no mínimo 6 caracteres');
      return;
    }

    if (newPassword !== confirmPassword) {
      setErrorMessage('As senhas não coincidem');
      return;
    }

    try {
      await updatePasswordMutation.mutateAsync({
        currentPassword,
        newPassword,
        confirmPassword,
      });

      setSuccessMessage('Senha alterada com sucesso!');

      // Clear form
      setCurrentPassword('');
      setNewPassword('');
      setConfirmPassword('');

      // Clear success message after 5 seconds
      setTimeout(() => setSuccessMessage(''), 5000);
    } catch (error: any) {
      setErrorMessage(
        error?.response?.data?.message || 'Erro ao alterar senha. Verifique a senha atual.'
      );
    }
  };

  // Publish the primary save action to the page header
  useAccountSaveButton({
    onSave: handleChangePassword,
    saving: updatePasswordMutation.isPending,
    label: 'Atualizar senha',
    loadingLabel: 'Alterando...',
  });

  return (
    <FormSection
      icon={'lock'}
      title="Segurança"
      description="Altere sua senha de acesso"
    >
      <form
        onSubmit={(e) => {
          e.preventDefault();
          void handleChangePassword();
        }}
        className="space-y-4"
        data-testid="account-security-form"
      >
        {/* Success Message */}
        {successMessage && (
          <div
            className="flex items-center gap-2 p-3 rounded-lg"
            style={{
              backgroundColor: 'var(--theme-success-background)',
              borderLeft: '4px solid var(--theme-success-color)',
            }}
            data-testid="account-security-success"
          >
            <Icon name="check-circle" className="w-5 h-5" style={{ color: 'var(--theme-success-color)' }} />
            <p className="text-sm" style={{ color: 'var(--theme-success-color)' }}>
              {successMessage}
            </p>
          </div>
        )}

        {/* Error Message */}
        {errorMessage && (
          <div
            className="flex items-center gap-2 p-3 rounded-lg"
            style={{
              backgroundColor: 'var(--theme-error-background)',
              borderLeft: '4px solid var(--theme-error-color)',
            }}
            data-testid="account-security-error"
          >
            <Icon name="alert-circle" className="w-5 h-5" style={{ color: 'var(--theme-error-color)' }} />
            <p className="text-sm" style={{ color: 'var(--theme-error-color)' }}>
              {errorMessage}
            </p>
          </div>
        )}

        {/* Current Password */}
        <FormField label="Senha Atual" htmlFor="currentPassword" required>
          <div className="relative">
            <Input
              id="currentPassword"
              type={showCurrentPassword ? 'text' : 'password'}
              value={currentPassword}
              onChange={(e) => setCurrentPassword(e.target.value)}
              placeholder="Digite sua senha atual"
              className="pr-10"
              data-testid="account-current-password-input"
            />
            <button
              type="button"
              onClick={() => setShowCurrentPassword(!showCurrentPassword)}
              className="absolute right-3 top-1/2 -translate-y-1/2"
              style={{ color: 'var(--theme-muted-text)' }}
              data-testid="account-toggle-current-password"
            >
              {showCurrentPassword ? (
                <Icon name="eye-off" className="w-4 h-4" />
              ) : (
                <Icon name="eye" className="w-4 h-4" />
              )}
            </button>
          </div>
        </FormField>

        {/* Nova + Confirmar lado a lado (mockup `.two` grid 1fr 1fr) */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        {/* New Password */}
        <FormField label="Nova Senha" htmlFor="newPassword" required>
          <div className="relative">
            <Input
              id="newPassword"
              type={showNewPassword ? 'text' : 'password'}
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
              placeholder="Digite a nova senha (mínimo 6 caracteres)"
              className="pr-10"
              data-testid="account-new-password-input"
            />
            <button
              type="button"
              onClick={() => setShowNewPassword(!showNewPassword)}
              className="absolute right-3 top-1/2 -translate-y-1/2"
              style={{ color: 'var(--theme-muted-text)' }}
              data-testid="account-toggle-new-password"
            >
              {showNewPassword ? (
                <Icon name="eye-off" className="w-4 h-4" />
              ) : (
                <Icon name="eye" className="w-4 h-4" />
              )}
            </button>
          </div>
        </FormField>

        {/* Confirm New Password */}
        <FormField label="Confirmar Nova Senha" htmlFor="confirmPassword" required>
          <div className="relative">
            <Input
              id="confirmPassword"
              type={showConfirmPassword ? 'text' : 'password'}
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              placeholder="Digite a nova senha novamente"
              className="pr-10"
              data-testid="account-confirm-password-input"
            />
            <button
              type="button"
              onClick={() => setShowConfirmPassword(!showConfirmPassword)}
              className="absolute right-3 top-1/2 -translate-y-1/2"
              style={{ color: 'var(--theme-muted-text)' }}
              data-testid="account-toggle-confirm-password"
            >
              {showConfirmPassword ? (
                <Icon name="eye-off" className="w-4 h-4" />
              ) : (
                <Icon name="eye" className="w-4 h-4" />
              )}
            </button>
          </div>
        </FormField>
        </div>

        {/* Hint */}
        <p className="text-xs" style={{ color: 'var(--theme-muted-text)' }}>
          A nova senha deve ter no mínimo 6 caracteres.
        </p>
      </form>
    </FormSection>
  );
};
